<template>
    <div class="address mb-3" :class="{ 'input-style-tailwind': inputStyle === 'tailwind' }">
        <h6 v-if="title" :class="inputStyle === 'tailwind' ? 'text-lg font-semibold mb-4' : 'text-overline font-weight-bold'">
            {{ title }}
        </h6>
        <v-row class="mt-0">
            <v-col cols="12" sm="6">
                <template v-if="inputStyle === 'tailwind'">
                    <div class="space-y-1.5">
                        <label :for="`address-contact_name-${_uid}`" class="text-xs font-semibold text-slate-500 uppercase tracking-wider">{{ $translate('contactname') }}</label>
                        <input
                            :id="`address-contact_name-${_uid}`"
                            type="text"
                            :value="address.contact_name"
                            :placeholder="$translate('contactnamePlaceholder')"
                            :disabled="readonly"
                            :class="[twInputClass, errors.contact_name && errors.contact_name.length ? 'border-red-500' : '']"
                            @input="address.contact_name = $event.target.value; $emit('input', address)"
                        />
                        <p v-if="errors.contact_name && errors.contact_name.length" class="text-xs text-red-600">{{ errors.contact_name[0] }}</p>
                    </div>
                </template>
                <v-text-field
                    v-else
                    v-model="address.contact_name"
                    :error-messages="errors.contact_name"
                    :label="$translate('contactname')"
                    :placeholder="$translate('contactnamePlaceholder')"
                    :disabled="readonly"
                    @input="$emit('input', address || {})"
                    :dense="true"
                    :outlined="outlined"
                        hide-details="auto"
                        clearable
                />
            </v-col>
            <v-col v-if="showEmails" cols="12" sm="6">
                <div :class="[{ 'space-y-1.5': inputStyle === 'tailwind' }, requiredHintClass(address.emails)]">
                    <label v-if="inputStyle === 'tailwind'" :for="`address-emails-${_uid}`" class="text-xs font-semibold text-slate-500 uppercase tracking-wider block">{{ $translate('emails') }}{{ requiredLabels ? ' *' : '' }}</label>
                    <v-combobox
                        :id="`address-emails-${_uid}`"
                        :append-icon="null"
                        hide-no-data
                        :disabled="readonly"
                        :error-messages="
                            emailsErrors && flatEmailsErrors ||
                            (highlightMandatory && (!address.emails || address.emails.length === 0)
                                ? [$translate('required')]
                                : null)
                        "
                        :hint="inputStyle !== 'tailwind' ? $translate('enterEmailAndPressTab') : null"
                        :label="inputStyle === 'tailwind' ? null : $translate('emails')"
                        :placeholder="fieldPlaceholder('enterEmailAndPressTab', true)"
                        :persistent-placeholder="showPersistentRequiredHint(true)"
                        :value="address.emails"
                        @input="setEmails($event)"
                        clearable
                        deletable-chips
                        ref="emails"
                        dense
                        :outlined="outlined"
                        multiple
                        hide-details="auto"
                    >
                    <template v-slot:selection="{ attrs, item, select, selected }">
                        <v-chip
                            v-bind="attrs"
                            :input-value="selected"
                            close
                            @click="select"
                            @click:close="removeEmail(item)"
                            small
                            class="mx-1 primary-lighter3"
                        hide-details="auto"
                        >
                            {{ item }}
                        </v-chip>
                    </template>
                </v-combobox>
                </div>
            </v-col>
            <v-col cols="12" sm="6">
                <div :class="[{ 'space-y-1.5': inputStyle === 'tailwind' }, requiredHintClass(address.line1)]">
                    <label v-if="inputStyle === 'tailwind'" :for="`address-line1-${_uid}`" class="text-xs font-semibold text-slate-500 uppercase tracking-wider block">{{ $translate('address') }}{{ requiredLabels ? ' *' : '' }}</label>
                    <GoogleAddressAutocomplete
                        :input-id="`address-line1-${_uid}`"
                        v-model="address.line1"
                        :error-messages="
                            errors.line1 ||
                            (highlightMandatory && !address.line1
                                ? [$translate('required')]
                                : null)
                        "
                        :label="inputStyle === 'tailwind' ? null : $translate('address')"
                        :placeholder="fieldPlaceholder('addressPlaceholder', true)"
                        :persistent-placeholder="showPersistentRequiredHint(true)"
                        :placeholder-hint-active="showRequiredIndicators && !address.line1"
                        :disabled="readonly"
                        :dense="true"
                        :outlined="outlined"
                        :input-style="inputStyle"
                        hide-details="auto"
                        @address-selected="handleAddressSelected"
                        @input="$emit('input', address || {})"
                    />
                </div>
            </v-col>

            <v-col cols="12" sm="6">
                <template v-if="inputStyle === 'tailwind'">
                    <div class="space-y-1.5">
                        <label :for="`address-line2-${_uid}`" class="text-xs font-semibold text-slate-500 uppercase tracking-wider">{{ $translate('addressLine2') }}</label>
                        <input
                            :id="`address-line2-${_uid}`"
                            type="text"
                            :value="address.line2"
                            :placeholder="$translate('addressLine2Placeholder')"
                            :disabled="readonly"
                            :class="[twInputClass, errors.line2 && errors.line2.length ? 'border-red-500' : '']"
                            @input="address.line2 = $event.target.value; $emit('input', address)"
                        />
                        <p v-if="errors.line2 && errors.line2.length" class="text-xs text-red-600">{{ errors.line2[0] }}</p>
                    </div>
                </template>
                <v-text-field
                    v-else
                    v-model="address.line2"
                    :error-messages="errors.line2"
                    :label="$translate('addressLine2')"
                    :placeholder="$translate('addressLine2Placeholder')"
                    :disabled="readonly"
                    @input="$emit('input', address || {})"
                    :dense="true"
                    :outlined="outlined"
                    hide-details="auto"
                    clearable
                />
            </v-col>
            <v-col cols="12" sm="6">
                <template v-if="inputStyle === 'tailwind'">
                    <div :class="['space-y-1.5', requiredHintClass(address.city)]">
                        <label :for="`address-city-${_uid}`" class="text-xs font-semibold text-slate-500 uppercase tracking-wider">{{ $translate('city') }}{{ requiredLabels ? ' *' : '' }}</label>
                        <input
                            :id="`address-city-${_uid}`"
                            type="text"
                            :value="address.city"
                            :placeholder="fieldPlaceholder('cityPlaceholder', true)"
                            :disabled="readonly"
                            :class="[twInputClass, (errors.city && errors.city.length) || (highlightMandatory && !address.city) ? 'border-red-500' : '']"
                            @input="address.city = $event.target.value; $emit('input', address)"
                        />
                        <p v-if="(errors.city && errors.city.length) || (highlightMandatory && !address.city)" class="text-xs text-red-600">{{ (errors.city && errors.city[0]) || $translate('required') }}</p>
                    </div>
                </template>
                <v-text-field
                    v-else
                    v-model="address.city"
                    :class="requiredHintClass(address.city)"
                    :error-messages="
                        errors.city ||
                        (highlightMandatory && !address.city
                            ? [$translate('required')]
                            : null)
                    "
                    :label="$translate('city')"
                    :placeholder="fieldPlaceholder('cityPlaceholder', true)"
                    :persistent-placeholder="showPersistentRequiredHint(true)"
                    :disabled="readonly"
                    @input="$emit('input', address || {})"
                    hide-details="auto"
                    :dense="true"
                    :outlined="outlined"
                    clearable
                />
            </v-col>
            <v-col cols="12" sm="3">
                <template v-if="inputStyle === 'tailwind'">
                    <div :class="['space-y-1.5', requiredHintClass(address.postal_code)]">
                        <label :for="`address-postal_code-${_uid}`" class="text-xs font-semibold text-slate-500 uppercase tracking-wider">{{ $translate('postalCode') }}{{ requiredLabels ? ' *' : '' }}</label>
                        <input
                            :id="`address-postal_code-${_uid}`"
                            type="text"
                            :value="address.postal_code"
                            :placeholder="fieldPlaceholder('postalCodePlaceholder', true)"
                            :disabled="readonly"
                            :class="[twInputClass, (errors.postal_code && errors.postal_code.length) || (highlightMandatory && !address.postal_code) ? 'border-red-500' : '']"
                            @input="address.postal_code = $event.target.value; $emit('input', address)"
                        />
                        <p v-if="(errors.postal_code && errors.postal_code.length) || (highlightMandatory && !address.postal_code)" class="text-xs text-red-600">{{ (errors.postal_code && errors.postal_code[0]) || $translate('required') }}</p>
                    </div>
                </template>
                <v-text-field
                    v-else
                    v-model="address.postal_code"
                    :class="requiredHintClass(address.postal_code)"
                    :error-messages="
                        errors.postal_code ||
                        (highlightMandatory && !address.postal_code
                            ? [$translate('required')]
                            : null)
                    "
                    :label="$translate('postalCode')"
                    :placeholder="fieldPlaceholder('postalCodePlaceholder', true)"
                    :persistent-placeholder="showPersistentRequiredHint(true)"
                    :disabled="readonly"
                    @input="$emit('input', address || {})"
                    :dense="true"
                    :outlined="outlined"
                    hide-details="auto"
                    clearable
                />
            </v-col>
           

            <v-col cols="12" sm="3">
                <div :class="[{ 'space-y-1.5': inputStyle === 'tailwind' }, requiredHintClass(address.country)]">
                    <label v-if="inputStyle === 'tailwind'" :for="`address-country-${_uid}`" class="text-xs font-semibold text-slate-500 uppercase tracking-wider block">{{ $translate('country') }}{{ requiredLabels ? ' *' : '' }}</label>
                    <CountrySelect
                        :input-id="`address-country-${_uid}`"
                        :value="address.country"
                        :error-messages="
                            errors.country ||
                            (highlightMandatory && !address.country
                                ? [$translate('required')]
                                : null)
                        "
                        :label="inputStyle === 'tailwind' ? null : $translate('country')"
                        :placeholder="fieldPlaceholder('countryPlaceholder', true)"
                        :persistent-placeholder="showPersistentRequiredHint(true)"
                        :disabled="readonly"
                        :dense="true"
                        :outlined="outlined"
                        :input-style="inputStyle"
                        @input="updateCountry"
                        hide-details="auto"
                    />
                    <p v-if="inputStyle === 'tailwind' && ((errors.country && errors.country.length) || (highlightMandatory && !address.country))" class="text-xs text-red-600">{{ (errors.country && errors.country[0]) || $translate('required') }}</p>
                </div>
            </v-col>
            <v-col cols="12" sm="6">
                <div :class="{ 'address-phone-field space-y-1.5': inputStyle === 'tailwind' }">
                    <label v-if="inputStyle === 'tailwind' || showPhoneLabels" :for="`address-mobile-${_uid}`" class="text-xs font-semibold text-slate-500 uppercase tracking-wider block">{{ $translate('mobileNumber') }}</label>
                    <vue-tel-input
                        :key="address.country"
                        :auto-default-country="false"
                        :value="address.mobile_phone_number || ''"
                        :disabled="readonly"
                        :defaultCountry="defaultCountryCode"
                        :inputOptions="{
                            id: `address-mobile-${_uid}`,
                            placeholder: $translate('mobileNumber'),
                            showDialCode: false,
                        }"
                        :validCharactersOnly="true"
                        mode="international"
                        @input="updateMobilePhoneNumber"
                        @validate="validateMobilePhoneNumber"
                        :class="{ 'error--text': mobilePhoneNumberErrors }"
                    />
                    <p v-if="mobilePhoneNumberErrors && mobilePhoneNumberErrors.length" class="text-xs text-red-600">{{ mobilePhoneNumberErrors[0] }}</p>
                </div>
            </v-col>
            <v-col cols="12" sm="6">
                <div :class="{ 'address-phone-field space-y-1.5': inputStyle === 'tailwind' }">
                    <label v-if="inputStyle === 'tailwind' || showPhoneLabels" :for="`address-phone-${_uid}`" class="text-xs font-semibold text-slate-500 uppercase tracking-wider block">{{ $translate('phoneNumber') }}</label>
                    <vue-tel-input
                        :key="defaultCountryCode"
                        :auto-default-country="false"
                        :value="address.phone_number || ''"
                        :disabled="readonly"
                        :default-country="defaultCountryCode"
                        :input-options="{
                            id: `address-phone-${_uid}`,
                            placeholder: $translate('phoneNumber'),
                            showDialCode: false,
                        }"
                        :valid-characters-only="true"
                        mode="international"
                        @input="updatePhoneNumber"
                        @validate="validatePhoneNumber"
                        :class="{ 'error--text': phoneNumberErrors }"
                    />
                    <p v-if="phoneNumberErrors && phoneNumberErrors.length" class="text-xs text-red-600">{{ phoneNumberErrors[0] }}</p>
                </div>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import { readonly } from "vue";
import { mapGetters } from "vuex";
import CountrySelect from "@/components/common/country-select.vue";
import { VueTelInput } from "vue-tel-input";
import GoogleAddressAutocomplete from "@/components/common/google-address-autocomplete.vue";
import { isEmptyRequiredValue, requiredPlaceholder } from "@/utils/required-field";
import "vue-tel-input/dist/vue-tel-input.css";

export default {
    components: {
        CountrySelect,
        VueTelInput,
        GoogleAddressAutocomplete,
    },
    props: {
        showPhoneLabels: Boolean,
        showEmails: { type: Boolean, default: true },
        title: {
            type: String,
            required: false,
        },
        value: {
            type: Object,
            required: true,
        },
        errors: {
            type: Object,
            required: true,
            default: () => ({}),
        },
        readonly: {
            type: Boolean,
            required: false,
            default: false,
        },
        requiredLabels: {
            type: Boolean,
            default: false,
        },
        highlightMandatory: {
            type: Boolean,
            required: false,
            default: false,
        },
        showRequiredIndicators: {
            type: Boolean,
            required: false,
            default: false,
        },
        outlined: {
            type: Boolean,
            default: false,
        },
        /** 'vuetify' (default) or 'tailwind' – controls input visual style */
        inputStyle: {
            type: String,
            default: 'vuetify',
            validator: (v) => ['vuetify', 'tailwind'].includes(v),
        },
    },
    watch: {
        "errors.mobile_phone_number": function (value) {
            if (value) {
                this.mobilePhoneNumberErrors = value;
            }
        },
        "errors.phone_number": function (value) {
            if (value) {
                this.phoneNumberErrors = value;
            }
        },
        "errors.emails": function (value) {
            if (value) {
                this.emailsErrors = value;
            }
        },
        value() {
            this.address = JSON.parse(
                JSON.stringify({ ...(this.value || {}) })
            );
        },
    },
    data() {
        return {
            address: { country: "FR", ...(this.value || {}) },
            emailsErrors: null,
            mobilePhoneNumberErrors: null,
            phoneNumberErrors: null,
            mobilePhoneValid: false,
            phoneValid: false,
        };
    },
    methods: {
        fieldPlaceholder(key, isRequired = false) {
            const text = this.$translate(key);
            if (!this.showRequiredIndicators || !isRequired) {
                return text;
            }
            return requiredPlaceholder(text, this.$translate("requiredSuffix"));
        },
        requiredHintClass(value) {
            return {
                "required-field-placeholder-hint":
                    this.showRequiredIndicators && isEmptyRequiredValue(value),
            };
        },
        showPersistentRequiredHint(isRequired = false) {
            return this.showRequiredIndicators && isRequired;
        },
        setEmails(value) {
            this.emailsErrors = null;
            this.address.emails = value;
            this.$emit("input", { ...this.address });
        },
        removeEmail(emailToRemove) {
            if (this.readonly) {
                return;
            }
            const emails = Array.isArray(this.address.emails) ? [...this.address.emails] : [];
            const index = emails.indexOf(emailToRemove);
            if (index > -1) {
                emails.splice(index, 1);
                this.setEmails(emails);
            }
        },
        updateCountry(value) {
            this.address.country = value;
            this.$emit("input", { ...this.address });
        },
        updateMobilePhoneNumber(number, phoneObject) {
            this.mobilePhoneNumberErrors = null;
            if (phoneObject?.valid) {
                this.address.mobile_phone_number = phoneObject.number;
            } else {
                this.address.mobile_phone_number = number;
            }
            this.$emit("input", { ...this.address });
        },
        validateMobilePhoneNumber(phoneObject) {
            this.mobilePhoneValid = phoneObject?.valid || false;
        },
        updatePhoneNumber(number, phoneObject) {
            this.phoneNumberErrors = null;
            if (phoneObject?.valid) {
                this.address.phone_number = phoneObject.number;
            } else {
                this.address.phone_number = number;
            }
            this.$emit("input", { ...this.address });
        },
        validatePhoneNumber(phoneObject) {
            this.phoneValid = phoneObject?.valid || false;
        },
        handleAddressSelected(addressData) {
            if (addressData) {
                // Update address fields with Google Places data
                this.address.line1 = addressData.line1 || this.address.line1;
                this.address.line2 = addressData.line2 || this.address.line2;
                this.address.postal_code =
                    addressData.postal_code || this.address.postal_code;
                this.address.city = addressData.city || this.address.city;
                this.address.country =
                    addressData.country || this.address.country;
                // Emit the updated address
                this.$emit("input", { ...this.address });
            }
        },
    },
    computed: {
        ...mapGetters("session", ["current_entity"]),
        twInputClass() {
            return 'w-full bg-slate-50 border border-slate-100 dark:border-slate-700 rounded text-sm px-3 py-2 focus:ring-2 focus:ring-primary focus:border-primary focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed';
        },
        defaultCountryCode() {
            // Get country code from current entity's billing address
            const entityCountry = this.address.country;
            return entityCountry || "FR";
        },

        flatEmailsErrors() {
            if (this.errors.emails) {
                return Object.values(this.errors.emails).flat();
            }
            if (this.highlightMandatory && !this.address.emails) {
                return [this.$translate("required")];
            }
            return null;
        },
    },
    i18n: {
        messages: {
            en: {
                contactname: "Contact name",
                address: "Address",
                postalCode: "Postal code",
                city: "City",
                country: "Country",
                mobileNumber: "Mobile number",
                phoneNumber: "Phone number",
                email: "Email",
                required: "Required",
                requiredSuffix: "Required",
                contactnamePlaceholder: "John Doe",
                enterEmailAndPressTab:
                    "Enter email and press TAB key to add email.",
                emails: "Email(s)",
                country_selector_label: "Country",
                country_selector_error: "Choose a country",
                example: "Example",
                addressPlaceholder: "Start typing an address...",
                cityPlaceholder: "City",
                postalCodePlaceholder: "Postal code",
                countryPlaceholder: "Select a country",
                addressLine2: "Address line 2",
                addressLine2Placeholder: "Apartment, suite, etc.",
            },
            fr: {
                contactname: "Nom du contact",
                address: "Adresse",
                postalCode: "Code postal",
                city: "Ville",
                country: "Pays",
                mobileNumber: "Téléphone portable",
                phoneNumber: "Numéro de téléphone",
                email: "Email",
                required: "Requis",
                requiredSuffix: "Requis",
                contactnamePlaceholder: "Jean Dupont",
                enterEmailAndPressTab:
                    "Entrez l'adresse e-mail à ajouter puis appuyez sur la touche TAB pour en ajouter une autre.",
                emails: "Email(s)",
                country_selector_label: "Pays",
                country_selector_error: "Choisir un pays",
                example: "Exemple",
                addressPlaceholder: "Commencez à taper une adresse...",
                cityPlaceholder: "Ville",
                postalCodePlaceholder: "Code postal",
                countryPlaceholder: "Sélectionnez un pays",
                addressLine2: "Complément d'adresse",
                addressLine2Placeholder: "Appartement, étage, etc.",
            },
        },
    },
};
</script>

<style lang="scss" scoped>
/* Very light border: 1px solid #f5f6f9 (still visible on slate-50 bg). */

/* Vuetify: match native border-radius (Tailwind rounded = 0.25rem). Force on root + slot + fieldset. */
.input-style-tailwind ::v-deep .v-input {
    border-radius: 0.25rem !important;
    overflow: hidden !important;
}
.input-style-tailwind ::v-deep .v-input .v-input__slot {
    background-color: #f8fafc !important;
    border: 1px solid #f5f6f9 !important;
    border-radius: 0.25rem !important;
    min-height: 40px !important;
    padding-left: 12px !important;
    padding-right: 12px !important;
}
.input-style-tailwind ::v-deep .v-input fieldset {
    border: none !important;
    border-radius: 0.25rem !important;
}
.input-style-tailwind ::v-deep .v-input.v-input--is-focused .v-input__slot {
    border-color: #3b82f6 !important;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3) !important;
}

/* Dark mode */
.dark .input-style-tailwind ::v-deep .v-input .v-input__slot {
    border-color: #334155 !important;
}

/* Native inputs */
.input-style-tailwind input.w-full {
    border: 1px solid #f5f6f9 !important;
}
.input-style-tailwind input.w-full:focus {
    border-color: #3b82f6 !important;
}

/* vue-tel-input: single border on wrapper, same radius as native (0.25rem) */
.input-style-tailwind ::v-deep .address-phone-field .vti,
.input-style-tailwind ::v-deep .address-phone-field .vue-tel-input,
.input-style-tailwind ::v-deep .vti,
.input-style-tailwind ::v-deep .vue-tel-input {
    border: 1px solid #f5f6f9 !important;
    border-radius: 0.25rem !important;
    overflow: hidden !important;
}
.input-style-tailwind ::v-deep .address-phone-field input,
.input-style-tailwind ::v-deep .vti input,
.input-style-tailwind ::v-deep .vue-tel-input input {
    background-color: #f8fafc !important;
    border: none !important;
    border-radius: 0.25rem !important;
    min-height: 40px !important;
    padding: 0.5rem 0.75rem !important;
    font-size: 0.875rem !important;
}
.input-style-tailwind ::v-deep .address-phone-field .vti:focus-within,
.input-style-tailwind ::v-deep .address-phone-field .vue-tel-input:focus-within,
.input-style-tailwind ::v-deep .vti:focus-within,
.input-style-tailwind ::v-deep .vue-tel-input:focus-within {
    border-color: #3b82f6 !important;
}
.input-style-tailwind ::v-deep .address-phone-field input:focus,
.input-style-tailwind ::v-deep .vti input:focus,
.input-style-tailwind ::v-deep .vue-tel-input input:focus {
    outline: none !important;
    box-shadow: none !important;
}
</style>
